import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { AppState } from 'src/app/store/state/app.state'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { KnowledgeAPIModel, PaginatedAPI, UserAPIModel } from 'src/app/models/api.model'
import {
  HEADING,
  VIEW_FOR_ADMIN,
  VIEW_FOR_EMPLOYEE,
  KNOWLEDGE_BANK_STATUS_SELECT,
  CREATED_ON, KNOWLEDGE_CATEGORY
} from 'src/app/constants/ui.columns'
import { getKnowledgeSearch } from 'src/app/store/selector/knowledge.selector'
import { LOAD_KNOWLEDGE_SUMMARY_DETAILS } from 'src/app/store/action/knowledge.action'
import { KnowledgeService } from 'src/app/services/knowledge/knowledge.service'
import { Observable, Subscription } from 'rxjs'
import { generateOptionsMapFromSet } from 'src/app/services/common/common.service'
import { CommonService, formatYesNoToBoolean } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { getTaskCategorySubCategoryMapFromState, getUserFromState } from 'src/app/store/selector/initialize.selector'

@Component({
  selector: 'app-knowledge-summary',
  templateUrl: './knowledge-summary.component.html',
  styleUrls: ['./knowledge-summary.component.scss']
})
export class KnowledgeSummaryComponent implements OnInit {
  summaryOptions: SummaryDataTableOptions
  userKnowledgeList$: Observable<PaginatedAPI<KnowledgeAPIModel>>
  summaryData: SummaryDataTableData<KnowledgeAPIModel>
  user$: Subscription
  user: UserAPIModel
  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'heading',
    sortOrder: 'ASC'
  }
  readonly userType = GlobalConstants.USER_TYPE
  private taskCategoryMap: Map<string, string>

  searchRequestBody: any = {
    age: undefined
  }

  constructor(
    private route: Router,
    private appState: Store<AppState>,
    private knowledgeService: KnowledgeService,
    private documentService: DocumentService,
    private cs: CommonService
  ) {
    this.userKnowledgeList$ = this.appState.select(getKnowledgeSearch())
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: false,
      contentSelectable: true,
      contentSelector: false,
      filterable: true,
      formattedContent: false,
      pageable: true,
      sortable: true,
      quickFilters: true,
      actionsColumn: {
        cssClass: 'actions-column',
        actions: [
          {
            name: 'Edit',
            callback: data => this.openEditScreen(data),
            showCondition: data => true
          },
          {
            name: 'Publish',
            callback: data => this.publishKnowledgeArticle(data),
            showCondition: data => data.status === 'DRAFT'
          }
        ]
      }
    }
    this.getUserDetailsFromStore()
  }

  getUserDetailsFromStore = () => {
    this.user$ = this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
      if (user) {
        this.user = user
        this.userBasedSummaryOption(this.user)
      }
    })
  }

  userBasedSummaryOption(user) {
    if (user?.userType === this.userType.RMS_ADMIN) {
      this.summaryOptions.additionalButtons = true
    }
  }

  ngOnInit(): void {
    this.knowledgeService.knowledgeRefresh.subscribe(response => {
      if (!response) {
        this.userKnowledgeList$ = this.appState.select(getKnowledgeSearch())
        this.loadSummaryData()
      }
    })
    this.appState
      .select(getTaskCategorySubCategoryMapFromState('NA'))
      .subscribe(categorySubCategoryMap => {
        if (categorySubCategoryMap) {
          const categoriesArr = Array.from(categorySubCategoryMap?.keys()).flatMap(array => array)
          const uniqueCategoriesArr = new Set(categoriesArr)
          this.taskCategoryMap = generateOptionsMapFromSet(uniqueCategoriesArr)
        }
      })
    this.summaryData = {
      content$: this.userKnowledgeList$,
      headers: [KNOWLEDGE_CATEGORY, HEADING, VIEW_FOR_EMPLOYEE, VIEW_FOR_ADMIN, CREATED_ON, KNOWLEDGE_BANK_STATUS_SELECT],
      additionalButtons: [],
      currentSearchParams: this.queryParamValues,
      sortCallback: this.sortCallback,
      checkBoxSelectionIds: [],
      searchCallback: this.searchCallback,
      onDropdownLimitChange: this.onDropdownLimitChange,
      selectPageCallback: this.selectPageCallback,
      downloadDocumentCallback: this.downloadDocumentCallback
    }
    this.addFiltersWithDynamicOptions()
  }

  searchCallback = searchParams => {
    if (searchParams.displayForEmployee) {
      searchParams.displayForEmployee = formatYesNoToBoolean(searchParams.displayForEmployee)
    }
    if (searchParams.displayForAdmin) {
      searchParams.displayForAdmin = formatYesNoToBoolean(searchParams.displayForAdmin)
    }

    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some(value => value !== undefined && value !== '')) {
      searchParams = {}
    }

    this.searchRequestBody = {
      ...searchParams
    }
    this.loadSummaryData()
  }

  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      const searchRequestBody = { ...this.searchRequestBody, age: $event.target.value }
      this.searchRequestBody = searchRequestBody
    } else {
      const searchRequestBody = { ...this.searchRequestBody, age: undefined }
      this.searchRequestBody = searchRequestBody
    }
    this.loadSummaryData()
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  private loadSummaryData() {
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(LOAD_KNOWLEDGE_SUMMARY_DETAILS(options))
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  back() {
    const searchPageLink = `/${GlobalConstants.ROUTING.KNOWLEDGE_HUB}/${GlobalConstants.ROUTING.SEARCH}`
    this.route.navigate([searchPageLink])
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  selectPageCallback = pageNum => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_KNOWLEDGE_SUMMARY_LIST_EXPORT + documentType}`
    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }
    this.documentService.downloadDocument(endPoint, options)
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  addFiltersWithDynamicOptions() {
    // Task Category
    KNOWLEDGE_CATEGORY.filter.getOptionsList = () => {
      return this.taskCategoryMap
    }
  }

  openEditScreen(data: any): void {
    this.cs.route(GlobalConstants.ROUTING.KNOWLEDGE_HUB, data.id, GlobalConstants.ROUTING.EDIT)
  }

  publishKnowledgeArticle(data: any): void {
    this.knowledgeService.dispatchPublishKnowledgeAction(data.id)
  }
}
